/**
 * Assigning ONE camera — its AI models, and the groups it belongs to — from the camera list.
 *
 * WHY THESE EXIST WHEN THERE ARE ALREADY PAGES FOR BOTH
 * ----------------------------------------------------
 * Model Assignment is a matrix of every group and every camera against every model, and the
 * Group Camera Map is a two-pane mover. Both are the right tool for setting a site up. Neither
 * is the right tool for the thing that actually happens all day: a camera is added, or moved,
 * or someone notices ONE camera is missing intrusion — and answering that meant leaving the
 * camera list, finding the row again in a matrix of a hundred, changing one checkbox, saving
 * the whole matrix, and coming back. These put the same two edits on the row itself.
 *
 * THEY ARE DELIBERATELY NOT A THIRD SOURCE OF TRUTH. Both write through the exact calls the
 * full pages use (`/models/assignments/bulk` scoped to this one camera, and the group
 * add/remove camera endpoints), so an edit made here and an edit made there are the same edit.
 *
 * PRESENTATIONAL ONLY: the host owns every request, because local talks to its backend
 * directly and cloud goes through the node proxy, and neither transport belongs in here.
 */
import { useMemo, useState } from 'react';
import Modal from '../common/Modal';
import { BTN_PRIMARY, BTN_SECONDARY, SEARCH_INPUT } from '../common/ui';
import {
  CheckIcon,
  CpuChipIcon,
  ExclamationCircleIcon,
  FolderIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

export interface AssignModelOption {
  id: string;
  name: string;
  description?: string | null;
  /** Off at the node level — still listed, but it can't run, and the row says so. */
  is_active?: boolean;
}

export interface AssignGroupOption {
  id: string;
  name: string;
  /** Nesting depth, for the indent. Hosts that keep a flat list can leave it out. */
  depth?: number;
  /** Ancestor names, joined for the row's second line — "Plant 1 › Floor 2". */
  path?: string;
}

/* ── The shell both dialogs share ─────────────────────────────────────────── */

/**
 * One modal chrome for both, so the two feel like one feature seen twice rather than two
 * dialogs that happen to open from the same menu. Escape closes; the backdrop closes unless a
 * save is in flight, since dismissing a dialog mid-write leaves the operator with no idea
 * whether it landed.
 */
function ModalShell({
  title,
  subtitle,
  icon,
  busy,
  error,
  footer,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  busy?: boolean;
  error?: string | null;
  footer: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  // The shared Modal shell (Escape, backdrop, scroll lock). Every way out is ignored while a
  // save is in flight, since dismissing mid-write leaves no idea whether it landed.
  const close = () => { if (!busy) onClose(); };
  return (
    <Modal
      open
      onClose={close}
      ariaLabel={title}
      size="md"
      title={(
        <span className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 text-muted-foreground">{icon}</span>
          <span className="truncate">{title}</span>
        </span>
      )}
      subtitle={subtitle ? <span title={subtitle}>{subtitle}</span> : undefined}
      footer={(
        <>
          {error && (
            <p className="-mx-5 -mt-3 mb-3 flex items-start gap-1.5 border-b border-border bg-red-50 px-5 py-2.5 text-[12px] text-red-700 dark:bg-red-950 dark:text-red-300">
              <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
              <span className="min-w-0">{error}</span>
            </p>
          )}
          <div className="flex items-center justify-between gap-3">{footer}</div>
        </>
      )}
    >
      {/* The list scrolls; the head and the foot do not. A site with sixty groups otherwise
          pushes Save off the bottom of the screen. */}
      <div className="px-2 py-2">{children}</div>
    </Modal>
  );
}

/** One selectable line — used for both models and groups so the two lists read identically. */
function CheckRow({
  checked,
  disabled,
  onToggle,
  title,
  note,
  right,
  indent = 0,
}: {
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
  title: string;
  note?: React.ReactNode;
  right?: React.ReactNode;
  indent?: number;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      aria-pressed={checked}
      style={indent ? { paddingLeft: 12 + indent * 16 } : undefined}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
        disabled ? 'cursor-default opacity-70' : 'hover:bg-muted'
      }`}
    >
      <span
        aria-hidden
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
          checked ? 'border-brand bg-brand text-white' : 'border-border bg-card'
        }`}
      >
        {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-foreground" title={title}>{title}</span>
        {note && <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{note}</span>}
      </span>
      {right}
    </button>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-10 text-center text-[13px] text-muted-foreground">{children}</p>;
}

/* ── Models ───────────────────────────────────────────────────────────────── */

export interface CameraModelsModalProps {
  cameraName: string;
  /** Every model the node has. */
  models: AssignModelOption[];
  /** Model ids assigned to THIS camera directly (not through a group). */
  assigned: string[];
  /**
   * Model ids this camera already gets from a group it belongs to → group names.
   *
   * Shown LOCKED rather than hidden or pre-ticked-and-editable, because both alternatives lie:
   * hiding it makes the operator assign a model the camera already runs, and an editable tick
   * suggests this dialog can take it away, which only editing the GROUP can do. Same rule the
   * Model Assignment page follows.
   */
  inheritedFrom?: Record<string, string[]>;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  /** The camera's DIRECT assignments after the edit — inherited ids are never included. */
  onSave: (modelIds: string[]) => void;
}

export function CameraModelsModal({
  cameraName, models, assigned, inheritedFrom = {}, busy, error, onClose, onSave,
}: CameraModelsModalProps) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(assigned));
  const [q, setQ] = useState('');

  const inheritedIds = useMemo(() => new Set(Object.keys(inheritedFrom)), [inheritedFrom]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? models.filter((m) => m.name.toLowerCase().includes(needle)) : models;
  }, [models, q]);

  /** Dirty is compared against what the camera HAS, not against "anything clicked": ticking a
   *  box and unticking it is not a change, and offering to save it invites a pointless write
   *  that restarts the camera's pipeline. */
  const dirty = useMemo(() => {
    const before = new Set(assigned);
    return picked.size !== before.size || [...picked].some((id) => !before.has(id));
  }, [picked, assigned]);

  const toggle = (id: string) => {
    if (inheritedIds.has(id)) return;
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const total = picked.size + inheritedIds.size;

  return (
    <ModalShell
      title="AI models for this camera"
      subtitle={cameraName}
      icon={<CpuChipIcon className="h-5 w-5" />}
      busy={busy}
      error={error}
      onClose={onClose}
      footer={
        <>
          <span className="text-[12px] text-muted-foreground">
            {total === 0 ? 'No models — this camera runs no analytics' : `${total} model${total === 1 ? '' : 's'} will run`}
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={BTN_SECONDARY}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave([...picked])}
              disabled={busy || !dirty}
              className={BTN_PRIMARY}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </span>
        </>
      }
    >
      {models.length > 8 && (
        <div className="relative px-2 pb-2 pt-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search models"
            className={SEARCH_INPUT}
          />
        </div>
      )}

      {!models.length ? (
        <EmptyLine>This node has no AI models installed.</EmptyLine>
      ) : !shown.length ? (
        <EmptyLine>No model matches “{q}”.</EmptyLine>
      ) : (
        shown.map((m) => {
          const via = inheritedFrom[m.id];
          const inherited = !!via;
          return (
            <CheckRow
              key={m.id}
              checked={inherited || picked.has(m.id)}
              disabled={inherited || busy}
              onToggle={() => toggle(m.id)}
              title={m.name}
              note={
                inherited
                  ? `Inherited from ${via.join(', ')} — change it on the group`
                  : m.is_active === false
                    ? 'Disabled on this node — assigned, but not running'
                    : m.description || undefined
              }
              right={inherited ? <LockClosedIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : undefined}
            />
          );
        })
      )}
    </ModalShell>
  );
}

/* ── Groups ───────────────────────────────────────────────────────────────── */

export interface CameraGroupsModalProps {
  cameraName: string;
  groups: AssignGroupOption[];
  /** Group ids this camera is currently a member of. */
  member: string[];
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  /**
   * The DIFF, not the new state: the node has an add endpoint and a remove endpoint and no
   * "set memberships" one, so the host would have to compute this anyway — and computing it
   * here means the host cannot get it subtly wrong in two places.
   */
  onSave: (change: { add: string[]; remove: string[] }) => void;
}

export function CameraGroupsModal({
  cameraName, groups, member, busy, error, onClose, onSave,
}: CameraGroupsModalProps) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(member));
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(needle) || (g.path || '').toLowerCase().includes(needle));
  }, [groups, q]);

  const change = useMemo(() => {
    const before = new Set(member);
    return {
      add: [...picked].filter((id) => !before.has(id)),
      remove: [...before].filter((id) => !picked.has(id)),
    };
  }, [picked, member]);
  const dirty = change.add.length > 0 || change.remove.length > 0;

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <ModalShell
      title="Groups for this camera"
      subtitle={cameraName}
      icon={<FolderIcon className="h-5 w-5" />}
      busy={busy}
      error={error}
      onClose={onClose}
      footer={
        <>
          {/* What the save will DO, in the same words as the buttons that will do it. A count
              of ticks would not distinguish "already in three groups" from "about to join
              three", and joining a group can hand the camera a new set of AI models. */}
          <span className="text-[12px] text-muted-foreground">
            {!dirty
              ? picked.size === 0 ? 'In no group' : `In ${picked.size} group${picked.size === 1 ? '' : 's'}`
              : [
                  change.add.length ? `+${change.add.length} to join` : null,
                  change.remove.length ? `−${change.remove.length} to leave` : null,
                ].filter(Boolean).join(' · ')}
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={BTN_SECONDARY}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(change)}
              disabled={busy || !dirty}
              className={BTN_PRIMARY}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </span>
        </>
      }
    >
      {groups.length > 8 && (
        <div className="relative px-2 pb-2 pt-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search groups"
            className={SEARCH_INPUT}
          />
        </div>
      )}

      {!groups.length ? (
        <EmptyLine>No groups on this node yet.</EmptyLine>
      ) : !shown.length ? (
        <EmptyLine>No group matches “{q}”.</EmptyLine>
      ) : (
        shown.map((g) => (
          <CheckRow
            key={g.id}
            checked={picked.has(g.id)}
            disabled={busy}
            onToggle={() => toggle(g.id)}
            title={g.name}
            note={g.path || undefined}
            /* Indent only in the unfiltered list: under a search the matches come from all
               over the tree, and indenting them by a depth whose parents aren't on screen
               draws a hierarchy that isn't there. */
            indent={q.trim() ? 0 : (g.depth || 0)}
          />
        ))
      )}
    </ModalShell>
  );
}
